import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create FormData object
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);

      // Send to backend
      const response = await axios.post(`${API}/booking-enquiry`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: "Enquiry Sent!",
        description: "We'll get back to you as soon as possible. Check your email for confirmation.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });

    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send enquiry. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent('Hi, I would like to enquire about horsebox hire.');
    window.open(`https://wa.me/447788754165?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 font-['Playfair_Display']">
            Get in <span className="text-[#2d5f3f]">Touch</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Ready to book your horsebox hire? Send us an enquiry or contact us directly. 
            We typically respond within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Cards */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#2d5f3f]/30 hover:shadow-lg transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-[#2d5f3f]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#2d5f3f]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Phone</h3>
                  <a href="tel:07788754165" className="text-gray-600 hover:text-[#2d5f3f] transition-colors duration-200">
                    07788 754165
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Mon-Sun, 8am-8pm</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#2d5f3f]/30 hover:shadow-lg transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-[#2d5f3f]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#2d5f3f]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Email</h3>
                  <a href="mailto:enquiries@equi-go.co.uk" className="text-gray-600 hover:text-[#2d5f3f] transition-colors duration-200 break-all">
                    enquiries@equi-go.co.uk
                  </a>
                  <p className="text-sm text-gray-500 mt-1">24-hour response time</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#2d5f3f]/30 hover:shadow-lg transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-[#2d5f3f]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#2d5f3f]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Location</h3>
                  <p className="text-gray-600">
                    North Yorkshire<br />
                    Between Harrogate and Boroughbridge
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp Button */}
            <button
              onClick={handleWhatsApp}
              className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-2xl p-6 flex items-center justify-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-xl group"
            >
              <MessageSquare className="w-6 h-6" />
              <span className="font-bold text-lg">Message on WhatsApp</span>
            </button>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Enquiry Form</h3>
              
              <div className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="w-full px-4 py-3 border-gray-300 focus:border-[#2d5f3f] focus:ring-[#2d5f3f]"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full px-4 py-3 border-gray-300 focus:border-[#2d5f3f] focus:ring-[#2d5f3f]"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="07788 754165"
                      className="w-full px-4 py-3 border-gray-300 focus:border-[#2d5f3f] focus:ring-[#2d5f3f]"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Please provide details about your enquiry..."
                    rows={5}
                    className="w-full px-4 py-3 border-gray-300 focus:border-[#2d5f3f] focus:ring-[#2d5f3f] resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2d5f3f] hover:bg-[#1e4029] text-white py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg group"
                >
                  {isSubmitting ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2 transition-transform duration-300 group-hover:translate-x-1" />
                      Send Enquiry
                    </>
                  )}
                </Button>
              </div>

              <p className="text-sm text-gray-500 mt-4 text-center">
                By submitting this form, you consent to us contacting you in relation to your enquiry. Your information will be processed in accordance with our{' '}
                <a href="/privacy-policy" className="text-[#2d5f3f] hover:underline">
                  Privacy Policy
                </a>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;